<script setup lang="ts">
import type { CarouselProps } from '#ui/types';
import lodashOmit from 'lodash-es/omit';
import { computed } from 'vue';

import {
    AuctionState,
    DirectSaleState,
    getApiAuctionsListingQueryKey,
    getApiDirectSalesListingQueryKey,
    type OrderByFieldDto,
    SalesListingSortBy,
    SortOrder,
    useApiAuctionsListing,
    useApiDirectSalesListing,
} from '~/apiClient';
import type { SaleListingItemEntity } from '~/types/sale.type';

export interface StoryblokSaleListingCarouselProps {
    blok: StoryblokSaleListingCarousel;
}

// PROPS
const { blok } = defineProps<StoryblokSaleListingCarouselProps>();

// DATA
const AUCTION_KINDS: ReadonlySet<StoryblokSaleListingCarousel['kind']> = new Set([
    'ending_soon_auctions',
    'featured_auctions',
    'featured_mixed',
    'trending_auctions',
]);
const DIRECT_SALE_KINDS: ReadonlySet<StoryblokSaleListingCarousel['kind']> = new Set([
    'featured_directSales',
    'featured_mixed',
    'trending_directSales',
]);
const KIND_TO_ORDER_BY_MAP: Record<StoryblokSaleListingCarousel['kind'], OrderByFieldDto[]> = {
    ending_soon_auctions: [{ field: SalesListingSortBy.expiresAt, order: SortOrder.desc }],
    featured_auctions: [],
    featured_directSales: [],
    featured_mixed: [],
    trending_auctions: [
        { field: SalesListingSortBy.priority, order: SortOrder.asc },
        { field: SalesListingSortBy.startAt, order: SortOrder.asc },
    ],
    trending_directSales: [
        { field: SalesListingSortBy.priority, order: SortOrder.asc },
        { field: SalesListingSortBy.startAt, order: SortOrder.asc },
    ],
};
const salesCount = computed(() => (blok.salesCount ? Number(blok.salesCount) : 8));
const auctionApiParams = computed(() => ({
    orderBy: KIND_TO_ORDER_BY_MAP[blok.kind],
    state: [AuctionState.Running],
    itemsPerPage: salesCount.value,
}));

const { data: auctionData, suspense: suspenseAuctions } = useApiAuctionsListing(auctionApiParams, {
    query: { enabled: computed(() => AUCTION_KINDS.has(blok.kind)), queryKey: getApiAuctionsListingQueryKey(auctionApiParams) },
});
AUCTION_KINDS.has(blok.kind) && (await suspenseAuctions());

const directSalesApiParams = computed(() => ({
    orderBy: KIND_TO_ORDER_BY_MAP[blok.kind],
    state: [DirectSaleState.Open],
    itemsPerPage: salesCount.value,
}));
const { data: directSaleData, suspense: suspenseDirectSales } = useApiDirectSalesListing(directSalesApiParams, {
    query: {
        enabled: computed(() => DIRECT_SALE_KINDS.has(blok.kind)),
        queryKey: getApiDirectSalesListingQueryKey(directSalesApiParams),
    },
});
DIRECT_SALE_KINDS.has(blok.kind) && (await suspenseDirectSales());

const sales = computed<SaleListingItemEntity[]>(() => {
    if (AUCTION_KINDS.has(blok.kind) && DIRECT_SALE_KINDS.has(blok.kind)) {
        return [...(auctionData.value?.auctions ?? []), ...(directSaleData.value?.directSales ?? [])].slice(0, salesCount.value);
    }

    if (AUCTION_KINDS.has(blok.kind)) return auctionData.value?.auctions ?? [];

    return directSaleData.value?.directSales ?? [];
});

const carouselProps = computed<Omit<CarouselProps, 'items'>>(() =>
    lodashOmit(storyblokOmitEmptyValues<StoryblokSaleListingCarousel>(blok), ['_uid', 'component', 'kind', 'title', 'salesCount', 'itemsPerPage'])
);
const ui = useStoryblokCarouselCarouselUi(() => blok.itemsPerPage ?? '4');
</script>

<template>
    <div v-if="sales.length" v-editable="blok" class="space-y-4">
        <h4 v-if="!!blok.title" class="text-center text-xl font-semibold">{{ blok.title }}</h4>
        <UCarousel v-slot="{ item }" v-bind="carouselProps" :items="sales" :ui="ui">
            <SlpCard :sale="item" />
        </UCarousel>
    </div>
</template>
